import { useEffect, useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, LayoutGrid, List as ListIcon, Search, Phone, MapPin, Wrench, Calendar as CalIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import InvoiceCreateDialog from "@/components/admin/InvoiceCreateDialog";
import api from "@/lib/api";

function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }

const statusColor = {
  scheduled: "bg-blue-500/15 text-blue-600",
  in_progress: "bg-purple-500/15 text-purple-600",
  completed: "bg-emerald-500/15 text-emerald-600",
};

export default function BookingsCalendarTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState("calendar"); // calendar | list
  const [q, setQ] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [invoiceJob, setInvoiceJob] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayInfo, setDayInfo] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/bookings/month", { params: { year, month } });
    setJobs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const bookingsByDate = useMemo(() => {
    const m = {};
    jobs.forEach(j => (j.slots || []).forEach(s => { if (!m[s.date]) m[s.date] = []; m[s.date].push({ ...s, job: j }); }));
    return m;
  }, [jobs]);

  const flatBookings = useMemo(() => {
    const rows = [];
    jobs.forEach(j => (j.slots || []).forEach(s => rows.push({ date: s.date, job: j })));
    rows.sort((a, b) => a.date.localeCompare(b.date));
    if (q) {
      const qq = q.toLowerCase();
      return rows.filter(r => r.job.customer_name?.toLowerCase().includes(qq) || r.job.address?.toLowerCase().includes(qq));
    }
    return rows;
  }, [jobs, q]);

  const totalDays = daysInMonth(year, month);
  const firstDow = new Date(year, month - 1, 1).getDay();
  const monthName = new Date(year, month - 1, 1).toLocaleString(undefined, { month: "long" });
  const dateStr = (d) => `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const openDate = async (ds) => {
    setSelectedDate(ds);
    setSelectedJob(null);
    try { const { data } = await api.get("/admin/bookings/slots", { params: { date: ds } }); setDayInfo(data); }
    catch { setDayInfo(null); }
  };

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const setStatus = async (jobId, status) => {
    await api.post(`/admin/jobs/${jobId}/status`, { status });
    load();
    if (selectedJob) {
      const updated = { ...selectedJob, status };
      setSelectedJob(updated);
      if (status === "completed" && !updated.invoice_id) setInvoiceJob(updated);
    }
  };

  const jobsForSelectedDate = selectedDate ? (bookingsByDate[selectedDate] || []) : [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Bookings</h1>
          <p className="text-sm text-muted-foreground mt-1">Mon–Fri open · Sat on demand · Sun closed.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-10 w-56 h-10" data-testid="bookings-search" />
          </div>
          <div className="flex gap-1 bg-card border border-border rounded-md p-1">
            <button onClick={() => setView("calendar")} data-testid="bookings-view-calendar" className={`h-8 w-8 rounded flex items-center justify-center ${view === "calendar" ? "bg-eagle-gold text-eagle-ink" : "hover:bg-secondary"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button onClick={() => setView("list")} data-testid="bookings-view-list" className={`h-8 w-8 rounded flex items-center justify-center ${view === "list" ? "bg-eagle-gold text-eagle-ink" : "hover:bg-secondary"}`}><ListIcon className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {view === "calendar" ? (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-5">
            <Button variant="ghost" size="sm" onClick={prev} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="font-display text-2xl font-semibold">{monthName} {year}</div>
            <Button variant="ghost" size="sm" onClick={next} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d} className="text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: firstDow }).map((_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: totalDays }).map((_, i) => {
              const d = i + 1;
              const ds = dateStr(d);
              const dow = new Date(year, month - 1, d).getDay();
              const isSunday = dow === 0;
              const isSat = dow === 6;
              const items = bookingsByDate[ds] || [];
              const isToday = ds === new Date().toISOString().slice(0, 10);
              const isSelected = selectedDate === ds;
              return (
                <button key={d} onClick={() => !isSunday && openDate(ds)} disabled={isSunday} data-testid={`cal-day-${ds}`}
                  className={`aspect-square p-2 rounded-lg border transition-all text-left flex flex-col ${
                    isSelected ? "border-eagle-gold ring-2 ring-eagle-gold" :
                    isSunday ? "bg-secondary/50 border-transparent opacity-50 cursor-not-allowed" :
                    "border-border hover:border-eagle-gold"
                  } ${isToday ? "bg-eagle-gold/5" : ""}`}>
                  <div className={`text-sm font-semibold ${isToday ? "text-eagle-gold" : ""}`}>{d}</div>
                  {isSat && !isSunday && <div className="text-[9px] text-amber-600">on demand</div>}
                  <div className="mt-auto space-y-0.5">
                    {items.slice(0, 2).map((s, k) => (
                      <div key={k} className="text-[10px] px-1 py-0.5 rounded bg-eagle-gold/15 text-eagle-gold truncate font-medium">{s.job.customer_name}</div>
                    ))}
                    {items.length > 2 && <div className="text-[9px] text-muted-foreground">+{items.length - 2} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground"><tr>
              <th className="p-3">Date</th><th className="p-3">Customer</th><th className="p-3">Address</th><th className="p-3">Status</th>
            </tr></thead>
            <tbody>
              {flatBookings.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No bookings this month.</td></tr>}
              {flatBookings.map((r, i) => (
                <tr key={i} onClick={() => setSelectedJob(r.job)} className="border-t border-border hover:bg-secondary/40 cursor-pointer" data-testid={`booking-list-row-${r.job.job_id}`}>
                  <td className="p-3 font-medium">{r.date}</td>
                  <td className="p-3">{r.job.customer_name}</td>
                  <td className="p-3 max-w-xs truncate">{r.job.address}</td>
                  <td className="p-3"><Badge className={(statusColor[r.job.status] || "") + " border-0"}>{r.job.status.replace("_", " ")}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Day drawer (calendar view) */}
      <Sheet open={!!selectedDate && !selectedJob} onOpenChange={(o) => { if (!o) setSelectedDate(null); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-display text-2xl">{selectedDate}</SheetTitle></SheetHeader>
          {dayInfo && (
            <div className="mt-4 space-y-4">
              {dayInfo.on_demand && <Badge className="bg-amber-500/15 text-amber-600 border-0">Saturday · on demand</Badge>}
              {jobsForSelectedDate.length === 0 ? (
                <div className="text-sm text-muted-foreground">No bookings on this day.</div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{jobsForSelectedDate.length} booking(s)</div>
                  {jobsForSelectedDate.map((s, i) => (
                    <Card key={i} className="p-3 cursor-pointer hover:border-eagle-gold" onClick={() => setSelectedJob(s.job)} data-testid={`day-booking-${s.job.job_id}`}>
                      <div className="font-medium">{s.job.customer_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.job.address}</div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Booking detail drawer */}
      <Sheet open={!!selectedJob} onOpenChange={(o) => { if (!o) setSelectedJob(null); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {selectedJob && (
            <>
              <SheetHeader><SheetTitle className="font-display text-2xl">{selectedJob.customer_name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4">
                <Badge className={(statusColor[selectedJob.status] || "") + " border-0"}>{selectedJob.status.replace("_", " ")}</Badge>
                <div className="p-3 rounded-lg bg-secondary space-y-2 text-sm">
                  <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-eagle-gold mt-0.5 shrink-0" /><span>{selectedJob.address}</span></div>
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-eagle-gold" /><a href={`tel:${selectedJob.customer_phone}`} className="hover:text-eagle-gold">{selectedJob.customer_phone}</a></div>
                  <div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-eagle-gold" /><span>{selectedJob.num_cameras} cameras · {selectedJob.installation_type}</span></div>
                  <div className="flex items-center gap-2"><CalIcon className="h-4 w-4 text-eagle-gold" /><span>{(selectedJob.slots || []).map(s => s.date).join(", ")}</span></div>
                </div>
                <div className="p-3 rounded-lg border border-border">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Job value</div>
                  <div className="font-display text-2xl font-bold text-eagle-gold">${(selectedJob.price || 0).toLocaleString()}</div>
                </div>
                {selectedJob.notes && <div className="p-3 rounded-lg bg-secondary/50 border-l-2 border-eagle-gold text-sm whitespace-pre-wrap">{selectedJob.notes}</div>}
                <div className="pt-4 space-y-2">
                  {selectedJob.status === "scheduled" && <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setStatus(selectedJob.job_id, "in_progress")} data-testid="drawer-start-btn">Start job</Button>}
                  {(selectedJob.status === "scheduled" || selectedJob.status === "in_progress") && <Button className="w-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" onClick={() => setStatus(selectedJob.job_id, "completed")} data-testid="drawer-complete-btn">Mark complete</Button>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      <InvoiceCreateDialog job={invoiceJob} open={!!invoiceJob} onClose={() => setInvoiceJob(null)} onCreated={() => { load(); setInvoiceJob(null); }} />
    </div>
  );
}